import FlexFormContainer from "../common/FlexFormContainer";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  Flex,
  SelectField,
  TextAreaField,
  TextField,
  Text,
  Label,
} from "@aws-amplify/ui-react";
import DatePicker from "rsuite/DatePicker";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserQuery } from "../common/useUserQuery";
import { useEffect } from "react";
import { useVitalQuery, useVitalsCatalogQuery } from "./api/useVitalsQuery";
import { useVitalsPutMutation } from "./api/useVitalsMutation";
import { formatDate } from "../common/useDateFunctions";
const { z } = require("zod");

const putRequest = z.object({
  type: z.string().min(1, "Required").max(30),
  date: z.date({ message: "Required" }),
  value: z.string().min(1, "Required").max(15),
  unit: z.string().min(1, "Required").max(30),
  notes: z.string().max(250).nullish(),
});

export default function VitalsEditPage() {
  const navigate = useNavigate();
  const params = useParams();

  const { data: userQueryData } = useUserQuery();
  const { data, isLoading } = useVitalQuery(
    params.id,
    userQueryData?.patientUserId
  );
  const { data: vitalsCatalog, isLoading: isLoadingVitalsCatalog } =
    useVitalsCatalogQuery();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(putRequest),
  });
  const { mutate: vitalsPutMutation } = useVitalsPutMutation();

  useEffect(() => {
    if (data) {
      reset({
        type: data?.type,
        date: data?.date ? new Date(`${data?.date}T00:00:00`) : null,
        value: data?.value,
        unit: data?.unit,
        notes: data?.notes,
      });
    }
  }, [data]);

  const onSubmit = (values) => {
    vitalsPutMutation({
      ...values,
      id: params.id,
      date: formatDate(new Date(values.date)),
    });

    reset();
    navigate("/user/hr/vitals");
  };

  if (isLoading || isLoadingVitalsCatalog) return "Loading...";

  if (data) {
    return (
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexFormContainer headerTitle={"Vitals Details"}>
            <Flex
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <SelectField
                    label="Type"
                    placeholder="Select"
                    {...field}
                    onChange={(e) => {
                      const vitalCatalogItem = vitalsCatalog?.find(
                        (vc) => vc?.name === e.target.value
                      );
                      if (vitalCatalogItem) {
                        const parsedUnits = JSON.parse(vitalCatalogItem?.units);
                        setValue("unit", parsedUnits[0]);
                      }
                      field.onChange(e);
                    }}
                    errorMessage={errors.type?.message}
                    hasError={!!errors.type}
                  >
                    {vitalsCatalog?.map((vc) => (
                      <option key={`vc_${vc?.id}`} value={vc?.name}>
                        {vc?.name}
                      </option>
                    ))}
                  </SelectField>
                )}
              />

              <Label htmlFor="date">Date</Label>

              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <Flex direction={"column"}>
                    <DatePicker
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      format="yyyy-MM-dd"
                      oneTap
                    />
                    {!!errors?.date && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.9em"
                        fontStyle="normal"
                        textDecoration="none"
                      >
                        {errors?.date.message}
                      </Text>
                    )}
                  </Flex>
                )}
              />

              <Controller
                name="value"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Value"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.value?.message}
                    hasError={!!errors.value}
                  />
                )}
              />

              <Controller
                name="unit"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Unit"
                    {...field}
                    disabled={true}
                    value={field.value || ""}
                    errorMessage={errors.unit?.message}
                    hasError={!!errors.unit}
                  />
                )}
              />

              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    label="Notes"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.notes?.message}
                    hasError={!!errors.notes}
                  />
                )}
              />

              <Button variation="primary" type="submit">
                Save
              </Button>
            </Flex>
          </FlexFormContainer>
        </form>
      </>
    );
  }
}
